<script setup lang="ts">
import {Export, ExtendedUser} from "@/types";
import BaseLayout from "@/layouts/BaseLayout.vue";
import ProfileLayout from "@/Pages/settings/layouts/ProfileLayout.vue";
import {Head, router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {useDate} from "@/composables/date";
import DeleteAccountDialog from "@/Pages/settings/account/components/DeleteAccountDialog.vue";

interface Props {
    user: ExtendedUser,
    exports: Array<Export>
}

const props = defineProps<Props>()
defineOptions({
    layout: BaseLayout
})

const {lastUsed} = useDate()

const requestExport = () => {
    router.post(route('settings.data_exports.store'))
}

const requestDeletion = (id: string) => {
    router.delete(route('settings.data_exports.destroy', {data_export: id}))
}

const requestEmail = (id: string) => {
    router.put(route('settings.data_exports.update', {data_export: id}))
}
</script>

<template>
    <Head title="Account settings" />
    <profile-layout
        :user="props.user"
    >
        <h1 class="font-weight-medium text-h5 mt-5">
            Export account data
        </h1>
        <v-divider class="my-5" />
        Export profile metadata for <b>{{ props.user.name }}</b>. Exports will be available for 7 days.
        <v-list
            rounded="lg"
            border="sm"
            class="py-0 mt-3"
        >
            <v-list-item
                class="bg-grey-lighten-3"
                min-height="64"
            >
                <span class="font-weight-bold text-body-2">Recent exports</span>
                <template #append>
                    <default-btn
                        class="border-sm"
                        rounded="lg"
                        size="small"
                        @click="requestExport"
                    >
                        New export
                    </default-btn>
                </template>
            </v-list-item>
            <v-list-item
                v-for="(dataExport, index) in props.exports"
                :key="index"
                :border="index < props.exports.length - 1 ? 'b' : undefined"
                min-height="64"
            >
                <span
                    v-if="dataExport.completed_at"
                    class="text-body-2"
                >{{ lastUsed(dataExport.completed_at) }}</span>
                <template #append>
                    <div
                        v-if="dataExport.deleted_at"
                        class="text-body-2 mr-10"
                    >
                        Download deleted
                    </div>
                    <div
                        v-if="dataExport.completed_at === null && dataExport.deleted_at === null"
                        class="text-body-2 mr-5"
                    >
                        <v-icon color="warning">
                            mdi-circle-small
                        </v-icon>We're preparing your export! We'll send you an email when it's finished.
                    </div>
                    <default-btn
                        v-if="dataExport.deleted_at === null && dataExport.completed_at !== null"
                        class="border-sm text-error mr-5"
                        rounded="lg"
                        size="small"
                        @click="requestDeletion(dataExport.id)"
                    >
                        Delete
                    </default-btn>
                    <default-btn
                        v-if="dataExport.deleted_at === null && dataExport.completed_at !== null"
                        class="border-sm"
                        rounded="lg"
                        size="small"
                        @click="requestEmail(dataExport.id)"
                    >
                        Resend email with link
                    </default-btn>
                </template>
            </v-list-item>
        </v-list>
        <h1 class="font-weight-bold text-h5 mt-10 text-error">
            Delete account
        </h1>
        <v-divider class="my-5" />
        Once you delete your account, there is no going back. Please be certain.
        <DeleteAccountDialog
            :user="props.user"
        />
        <legend class="text-caption text-grey-darken-2">
            Make sure to export your data before deleting your account.
        </legend>
    </profile-layout>
</template>

<style scoped>

</style>
